"use client";
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

interface ProcessSectionProps {
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
}

export default function ProcessSection({ isVisible, onVisibilityChange }: ProcessSectionProps) {
  const sectionRef = useRef(null);
  const t = useTranslations();
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisibilityChange(true);
        }
      },
      {
        threshold: 0.02,
        rootMargin: '0px 0px 50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [onVisibilityChange]);

  return (
    <>
      {/* Process Section Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .process-bg-1, .process-bg-2 {
          filter: blur(300px);
        }

        @media (max-width: 768px) {
          .process-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          
          .section-padding {
            padding: 60px 16px 40px 16px !important;
          }
          
          .section-title {
            font-size: 28px !important;
            text-align: center !important;
          }
          
          .process-bg-1, .process-bg-2 {
            filter: none !important;
            border-radius: 50% !important;
            transform: none !important;
            width: 300px !important;
            height: 300px !important;
          }
          
          .process-bg-1 {
            background: radial-gradient(circle at center, 
              rgba(255, 0, 21, 0.4) 0%, 
              rgba(255, 0, 21, 0.25) 15%, 
              rgba(255, 0, 21, 0.15) 30%, 
              rgba(255, 0, 21, 0.08) 45%, 
              rgba(255, 0, 21, 0.04) 60%, 
              rgba(255, 0, 21, 0.02) 75%, 
              rgba(255, 0, 21, 0.01) 85%, 
              transparent 100%) !important;
            left: 50px !important;
            top: 200px !important;
          }
          
          .process-bg-2 {
            background: radial-gradient(circle at center, 
              rgba(255, 0, 21, 0.4) 0%, 
              rgba(255, 0, 21, 0.25) 15%, 
              rgba(255, 0, 21, 0.15) 30%, 
              rgba(255, 0, 21, 0.08) 45%, 
              rgba(255, 0, 21, 0.04) 60%, 
              rgba(255, 0, 21, 0.02) 75%, 
              rgba(255, 0, 21, 0.01) 85%, 
              transparent 100%) !important;
            right: 50px !important;
            top: 400px !important;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 24px !important;
          }
          
          .section-padding {
            padding: 50px 12px 30px 12px !important;
          }
        }
      `}} />

      <section 
        ref={sectionRef} 
        style={{
          background: "#000",
          padding: "80px 24px 40px 24px",
          position: "relative",
        }}
        className="section-padding"
      >
        {/* Background gradients */}
        <div 
          className="process-bg-1"
          style={{
            position: "absolute",
            left: "100px",
            top: "300px",
            transform: "rotate(-135deg)",
            width: "400px",
            height: "400px",
            background: "linear-gradient(135deg,rgb(255, 0, 21) 70%, #090909 100%)",
            borderRadius: "50px",
            zIndex: 1,
            pointerEvents: "none",
            opacity: isVisible ? 1 : 0,
            transition: "opacity 2s ease-out",
            transitionDelay: "0.1s",
          }} 
        />

        <div 
          className="process-bg-2"
          style={{
            position: "absolute",
            right: "100px",
            top: "300px",
            transform: "rotate(135deg)",
            width: "400px",
            height: "400px",
            background: "linear-gradient(135deg,rgb(255, 0, 21) 70%, #090909 100%)",
            borderRadius: "50px",
            zIndex: 1,
            pointerEvents: "none",
            opacity: isVisible ? 1 : 0,
            transition: "opacity 2s ease-out",
            transitionDelay: "0.2s",
          }} 
        />

        <div style={{
          maxWidth: "1000px",
          margin: "0 auto",
          position: "relative",
          zIndex: 2,
        }}>
          {/* Content Grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "60px",
            alignItems: "flex-start",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.1s",
          }}
          className="process-grid">

            {/* Left Side - Title, Description & Image */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0px)' : 'translateX(-40px)',
              transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.2s",
            }}>
              <div>
                <h2 style={{
                  fontSize: "36px",
                  fontWeight: 600,
                  color: "#fff",
                  lineHeight: 1.2,
                  marginBottom: "16px",
                  textAlign: "left",
                }}
                className="section-title">
                  {t('oldFurnitureRemoval.process.title')}
                </h2>

                <p style={{
                  fontSize: "16px",
                  color: "rgba(255,255,255,0.8)",
                  lineHeight: 1.6,
                  textAlign: "left",
                  marginBottom: "40px",
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
                  transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: "0.3s",
                }}>
                  {t('oldFurnitureRemoval.process.subtitle')}
                </p>
              </div>

              {/* Image */}
              <div style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'scale(1)' : 'scale(0.95)',
                transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.4s",
              }}>
                <div style={{
                  position: "relative",
                  width: "320px",
                  height: "400px",
                }}>
                  <Image 
                    src="/images/components/phone-email.png"
                    alt="International moving process illustration"
                    fill
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Right Side - Accordions */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0px)' : 'translateX(40px)',
              transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.8s",
            }}>
              
              {/* Create all 5 process steps */}
              {[
                { key: 'contact', index: 0 },
                { key: 'quote', index: 1 },
                { key: 'scheduling', index: 2 },
                { key: 'execution', index: 3 },
                { key: 'payment', index: 4 }
              ].map(({ key, index }) => (
                <div 
                  key={index}
                  style={{
                    border: activeAccordion === index ? "none" : "1px solid #262626",
                    borderRadius: "12px",
                    background: activeAccordion === index 
                      ? "linear-gradient(135deg, #000000 0%, #880105 100%)" 
                      : "linear-gradient(135deg, #000000 0%, #0b0b0b 100%)",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                  }}
                >
                  <button
                    onClick={() => setActiveAccordion(activeAccordion === index ? null : index)}
                    style={{
                      width: "100%",
                      padding: "16px 20px",
                      background: "none",
                      border: "none",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: 600,
                      textAlign: "left",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "color 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (activeAccordion !== index) {
                        e.currentTarget.style.color = "rgba(255,255,255,0.9)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#fff";
                    }}
                  >
                    <div style={{ 
                      display: "flex",
                      alignItems: "center",
                      gap: "12px" 
                    }}>
                      {/* Circle with Number */}
                      <div style={{
                        position: "relative",
                        width: "32px",
                        height: "32px",
                        flexShrink: 0,
                      }}>
                        <Image 
                          src={activeAccordion === index 
                            ? "/images/components/red-circle.png" 
                            : "/images/components/grey-circle.png"
                          }
                          alt="Circle"
                          fill
                          style={{ 
                            objectFit: "contain",
                          }}
                        />
                        {/* Number inside circle */}
                        <div style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "#fff",
                          fontSize: "14px",
                          fontWeight: 700,
                          zIndex: 1,
                        }}>
                          {index + 1}
                        </div>
                      </div>
                      <span>{t(`oldFurnitureRemoval.process.steps.${key}.title`)}</span>
                    </div>
                    <span style={{
                      fontSize: "16px",
                      transform: activeAccordion === index ? "rotate(-180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}>
                      ▼
                    </span>
                  </button>

                  {activeAccordion === index && (
                    <div style={{
                      padding: "0 20px 20px 20px",
                      animation: "fadeIn 0.3s ease",
                    }}>
                      <div style={{
                        fontSize: "12px",
                        color: "rgba(255,255,255,0.8)",
                        lineHeight: 1.5,
                        paddingLeft: "44px", // Align with title text (32px circle + 12px gap)
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: "10px",
                        }}>
                          <div style={{
                            width: "12px",
                            height: "12px",
                            marginRight: "10px",
                            marginTop: "2px",
                            flexShrink: 0,
                            position: "relative"
                          }}>
                            <Image 
                              src="/images/components/check-2.png"
                              alt="Check mark"
                              fill
                              style={{ objectFit: "contain" }}
                            />
                          </div>
                          <span>{t(`oldFurnitureRemoval.process.steps.${key}.description`)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

            </div>
          </div>
        </div>
      </section>
    </>
  );
} 